import { LocationOpening, Location } from "../../../@types/DTO/Locations";
import { isTruthyLocationFlag } from "./locationFormatters";

export const WEEKDAY_KEYS_MON_FIRST = [
  "weekday.short.mon", // Lun
  "weekday.short.tue", // Mar
  "weekday.short.wed", // Mer
  "weekday.short.thu", // Gio
  "weekday.short.fri", // Ven
  "weekday.short.sat", // Sab
  "weekday.short.sun", // Dom
] as const;

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function coversDate(row: LocationOpening, isoDate: string): boolean {
  const from = row.validFrom || "0000-01-01";
  const to = row.validTo || "9999-12-31";
  return isoDate >= from && isoDate <= to;
}

interface WeekdayHours {
  /** 1..7 (Mon..Sun) */
  day: number;
  startTime: string;
  endTime: string;
}

interface DayRange {
  startDay: number; // 1..7
  endDay: number; // 1..7
  startTime: string;
  endTime: string;
}

/** Groups consecutive weekdays that share identical (startTime,endTime). */
function groupConsecutive(rows: WeekdayHours[]): DayRange[] {
  if (rows.length === 0) return [];
  const sorted = [...rows].sort((a, b) => a.day - b.day);
  const groups: DayRange[] = [];
  let current: DayRange = {
    startDay: sorted[0].day,
    endDay: sorted[0].day,
    startTime: sorted[0].startTime,
    endTime: sorted[0].endTime,
  };
  for (let i = 1; i < sorted.length; i++) {
    const r = sorted[i];
    const isConsecutive = r.day === current.endDay + 1;
    const sameHours =
      r.startTime === current.startTime && r.endTime === current.endTime;
    if (isConsecutive && sameHours) {
      current.endDay = r.day;
    } else {
      groups.push(current);
      current = {
        startDay: r.day,
        endDay: r.day,
        startTime: r.startTime,
        endTime: r.endTime,
      };
    }
  }
  groups.push(current);
  return groups;
}

export interface OpeningsSummaryOptions {
  t: (key: string) => string;
  anchorDate?: Date | null;
}

export function buildOpeningsSummary(
  location: Location | null | undefined,
  opts: OpeningsSummaryOptions
): string {
  if (!location) return "";
  const { t, anchorDate } = opts;

  if (isTruthyLocationFlag(location.isOutOfHours)) {
    return t("Open 24 hours a day, 7 days a week, including holidays.");
  }

  const openings = Array.isArray(location.openings) ? location.openings : [];
  if (openings.length === 0) return "";

  const anchor =
    anchorDate && !isNaN(anchorDate.getTime()) ? anchorDate : new Date();
  const isoAnchor = toIsoDate(anchor);

  const relevant: WeekdayHours[] = [];
  const seenDays = new Set<number>();
  for (const row of openings) {
    const dow = Number(row.dayOfTheWeek);
    if (!(dow >= 1 && dow <= 7)) continue;
    if (!coversDate(row, isoAnchor)) continue;

    if (seenDays.has(dow)) continue;
    seenDays.add(dow);
    relevant.push({
      day: dow,
      startTime: row.startTime || "",
      endTime: row.endTime || "",
    });
  }

  if (relevant.length === 0) return "";

  const groups = groupConsecutive(relevant);

  return groups
    .map((g) => {
      const startLabel = t(WEEKDAY_KEYS_MON_FIRST[g.startDay - 1]);
      if (g.startDay === g.endDay) {
        return `${startLabel} ${g.startTime}–${g.endTime}`;
      }
      const endLabel = t(WEEKDAY_KEYS_MON_FIRST[g.endDay - 1]);
      return `${startLabel}–${endLabel} ${g.startTime}–${g.endTime}`;
    })
    .join(", ");
}
